import { createInstance } from 'i18next';
import { invoice as enInvoice } from './locales/en/invoice.js';
import { glossary as enGlossary } from './locales/en/glossary.js';
import { activity as enActivity } from './locales/en/activity.js';
import { account as enAccount } from './locales/en/account.js';
import { chat as enChat } from './locales/en/chat.js';
import { project as enProject } from './locales/en/project.js';
import { errors as enErrors } from './locales/en/errors.js';
import { allocation as enAllocation } from './locales/en/allocation.js';
import { emptyState as enEmptyState } from './locales/en/empty-state.js';
import { omnibox as enOmnibox } from './locales/en/omnibox.js';
import { hub as enHub } from './locales/en/hub.js';
import { invoice as businessInvoice } from './locales/en-x-business/invoice.js';
import { glossary as businessGlossary } from './locales/en-x-business/glossary.js';
import { activity as businessActivity } from './locales/en-x-business/activity.js';
import { account as businessAccount } from './locales/en-x-business/account.js';
import { chat as businessChat } from './locales/en-x-business/chat.js';
import { project as businessProject } from './locales/en-x-business/project.js';
import { errors as businessErrors } from './locales/en-x-business/errors.js';
import { emptyState as businessEmptyState } from './locales/en-x-business/empty-state.js';
import { omnibox as businessOmnibox } from './locales/en-x-business/omnibox.js';
import { hub as businessHub } from './locales/en-x-business/hub.js';
import { NAMESPACES } from './namespaces.js';
// Compile-time-only (no runtime meaning beyond satisfying `noUnusedLocals`-style
// checks): fails to typecheck the moment `NAMESPACES` and the
// `CustomTypeOptions.resources` augmentation above disagree on the namespace
// set, in either direction.
const _namespacesMatchResources = true;
void _namespacesMatchResources;
const en = {
    invoice: enInvoice,
    glossary: enGlossary,
    activity: enActivity,
    account: enAccount,
    chat: enChat,
    project: enProject,
    errors: enErrors,
    allocation: enAllocation,
    emptyState: enEmptyState,
    omnibox: enOmnibox,
    hub: enHub,
};
// The business dialect is a SPARSE override catalog: a namespace may be
// omitted entirely (not just individual keys within it) when business needs
// no overrides there at all, so this is constrained as a partial record.
const enXBusiness = {
    invoice: businessInvoice,
    glossary: businessGlossary,
    activity: businessActivity,
    account: businessAccount,
    chat: businessChat,
    project: businessProject,
    errors: businessErrors,
    emptyState: businessEmptyState,
    omnibox: businessOmnibox,
    hub: businessHub,
};
/**
 * A single, module-load-time i18next instance carrying both locales' static
 * resources. `initAsync: false` (the current name for what was `initImmediate`
 * in older i18next releases - verified against the installed `i18next`
 * `InitOptions` type, which no longer declares `initImmediate`) plus
 * fully-static (no backend, no lazy-load) resources makes `init()` resolve
 * synchronously, so callers never need to await it.
 */
export const i18n = createInstance({
    resources: { en, 'en-x-business': enXBusiness },
    lng: 'en',
    fallbackLng: 'en',
    ns: NAMESPACES,
    defaultNS: 'invoice',
    interpolation: { escapeValue: false },
    initAsync: false,
    returnNull: false,
    // `strictKeyChecks` rejects a typo'd key at compile time, so a runtime miss
    // can only happen via a cast or an untyped string. In dev/test that is a bug
    // worth failing loudly for; in production, a crash is worse than a visible
    // key string, so `t()`'s long-standing fallback (returning the key) stands.
    // `parseMissingKeyHandler` (not `missingKeyHandler`, which requires
    // `saveMissing: true` and a save-missing backend) is the hook that actually
    // fires for a miss against these static resources - verified against the
    // installed `i18next` `InitOptions` type and by the dev/prod unit tests in
    // `instance.test.ts`. `appendNamespaceToMissingKey` makes that handler (and
    // the production fallback return value) carry the full `ns:key`, not just
    // the bare key - i18next strips the namespace before calling the handler
    // otherwise, which would name an ambiguous bare key across namespaces.
    //
    // `typeof process` guards this package's frontend consumers (apps/web/bill):
    // Vite statically replaces `process.env.NODE_ENV` for bundled dependencies,
    // so this collapses to a literal at build time there; the guard only
    // matters if some other consumer's bundler does not shim `process`, in
    // which case the safer default is the non-throwing (production) branch -
    // a process-less environment takes that branch FIRST, before the
    // dev/test-vs-production check below (which needs `process.env` to answer).
    appendNamespaceToMissingKey: true,
    parseMissingKeyHandler: (key) => {
        if (typeof process === 'undefined') {
            return key;
        }
        const isProduction = process.env?.NODE_ENV === 'production';
        if (!isProduction) {
            throw new Error(`@packages/copy: missing translation key "${key}" - only reachable via a cast or an unchecked string, since strictKeyChecks rejects a typo'd key at compile time.`);
        }
        return key;
    },
});
void i18n.init();
